import { FormattedDate, useLocale } from "@dgs/core";
import * as Sentry from "@sentry/react";
import { eachDayOfInterval, eachWeekOfInterval, endOfWeek, getWeek, isSameMonth, isToday, startOfWeek } from "date-fns";
import { MouseEvent, useEffect, useMemo } from "react";
import {
	CalendarCardBody,
	DayCell,
	EventGroup,
	EventName,
	Grid,
	GridContainer,
	HeaderCalendarRowContent,
	HeaderGrid,
	HolidayIcon,
	ICalendarEvent,
	LaneEvent,
	LaneGrid,
	LaneIcon,
	LaneParticipationStatusIcon,
	WeekIndicator,
	Weeks,
	groupEventsIntoLanes,
} from "./CalendarComponents";
import { useCalendarProvider } from "./CalendarProvider";

interface Props<T extends ICalendarEvent> {
	events: T[];
	onEventClick?: (event: MouseEvent<HTMLElement>, calendarEvent: T) => void;
}

export const CalendarBody = <T extends ICalendarEvent>({ events, onEventClick }: Props<T>) => {
	const {
		selectedDate,
		startOfMonthAndWeek,
		endOfMonthAndWeek,
		settings: { showCalendarWeeks },
		weekOptions,
	} = useCalendarProvider();
	const { language } = useLocale();
	const week = useMemo(
		() =>
			eachDayOfInterval(
				{
					start: startOfWeek(new Date(), weekOptions),
					end: endOfWeek(new Date(), weekOptions),
				},
				weekOptions,
			),
		[weekOptions],
	);
	const days = useMemo(
		() =>
			eachDayOfInterval(
				{
					start: startOfMonthAndWeek,
					end: endOfMonthAndWeek,
				},
				weekOptions,
			).map((date) => ({
				date: date,
				isDifferentMonth: !isSameMonth(date, selectedDate),
				isCurrentDay: isToday(date),
			})),
		[endOfMonthAndWeek, selectedDate, startOfMonthAndWeek, weekOptions],
	);
	const [eventBlocks, maxLanes] = useMemo(() => {
		const eventBlocks = groupEventsIntoLanes(
			startOfMonthAndWeek,
			endOfMonthAndWeek,
			events,
			weekOptions,
			showCalendarWeeks,
		);

		return [
			eventBlocks,
			eventBlocks.length === 0 ? 0 : eventBlocks.map((x) => x.lane).sort((a, b) => a - b)[eventBlocks.length - 1] + 1,
		];
	}, [events, showCalendarWeeks, startOfMonthAndWeek, endOfMonthAndWeek, weekOptions]);
	const weeks = eachWeekOfInterval({ start: startOfMonthAndWeek, end: endOfMonthAndWeek }, weekOptions).map((x) =>
		getWeek(x, weekOptions),
	);
	const lanesExceeds5 = eventBlocks.some((x) => x.lane + 1 > 5);

	useEffect(() => {
		if (lanesExceeds5) {
			Sentry.captureMessage("EVENT_CALENDAR_LANES_EXCEEDS_5");
		}
	}, [lanesExceeds5]);

	return (
		<CalendarCardBody>
			<HeaderGrid $showCalendarWeeks={showCalendarWeeks} $maxLanes={maxLanes}>
				{showCalendarWeeks && <div />}
				{week.map((date) => (
					<HeaderCalendarRowContent key={date.toISOString()}>
						{Intl.DateTimeFormat(language, { weekday: "short" }).format(date)}
					</HeaderCalendarRowContent>
				))}
			</HeaderGrid>
			<GridContainer>
				<Grid $showCalendarWeeks={showCalendarWeeks} $maxLanes={maxLanes}>
					{showCalendarWeeks && (
						<Weeks $end={weeks.length + 1}>
							{weeks.map((week) => (
								<WeekIndicator key={week} title={`KW ${week}`}>
									{week}
								</WeekIndicator>
							))}
						</Weeks>
					)}
					{days.map((date) => (
						<DayCell
							key={date.date.toISOString()}
							$isDifferentMonth={date.isDifferentMonth}
							$isCurrentDay={date.isCurrentDay}
						>
							<FormattedDate date={date.date} format={{ day: "numeric" }} />
						</DayCell>
					))}
				</Grid>
				<LaneGrid $showCalendarWeeks={showCalendarWeeks} $maxLanes={maxLanes}>
					{showCalendarWeeks && <div />}
					{eventBlocks.map((event, idx) => (
						<LaneEvent key={`${event.id}-${idx}`} $column={event.column} $row={event.row} $lane={event.lane}>
							<EventGroup
								onClick={(e) => onEventClick && onEventClick(e, event)}
								$color={event.color}
								$isHoliday={event.type === "holiday"}
							>
								{event.type === "holiday" ? (
									<HolidayIcon $icon="calendar" />
								) : (
									event.isHighlight && <LaneIcon $icon="star_filled" $color={event.color} />
								)}
								{event.participationStatus !== "none" && (
									<LaneParticipationStatusIcon
										hasParticipation={event.participationStatus === "withParticipation"}
										$color={event.color}
									/>
								)}
								<EventName title={event.name}>{event.name}</EventName>
							</EventGroup>
						</LaneEvent>
					))}
				</LaneGrid>
			</GridContainer>
		</CalendarCardBody>
	);
};
